import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import classnames from 'classnames';

import { LoadingIndicator } from 'ui-components/components/loading-indicator';

import { Text, TextType } from '../../../../ui-components/components/text';
import Global from '../../../../store/global-selectors';
import { customersOrdersGrid } from '../../../customers-orders-grid/columns';
import messages from '../../messages';
import style from './customer-previous-orders.module.less';

const PermissionMessage = () => (
  <div className={classnames(style['empty-state'], style['with-top-border'])}>
    <Text textType={TextType.Paragraph} value={messages.orderPermissionRequired} />
  </div>
);

const HeaderRow = () => (
  <>
    {customersOrdersGrid.map((column) => (
      <div key={column.id} className={style['header-cell']}>
        {column.getName()}
      </div>
    ))}
  </>
);

/**
 * Renders a single order row with all column details
 * @param {object} props
 * @param {object} props.order - Order object to display
 * @returns {React.ReactElement}
 */
const OrderDetail = ({ order }) => (
  <>
    {customersOrdersGrid.map((column) => (
      <div key={column.id} className={style['item-cell']}>
        {column.render(order)}
      </div>
    ))}
  </>
);

/**
 * Displays previous orders for a customer with loading state and empty state
 * @param {object} props
 * @param {string} [props.containerClassName] - Optional CSS class for the container
 * @param {string} [props.wrapperClassName] - Optional CSS class for the wrapper
 * @returns {React.ReactElement}
 */
export const CustomerPreviousOrders = ({ containerClassName, wrapperClassName }) => {
  const hasOrderManagementRole = useSelector(Global.selectUserhasOrderManagementRole);
  const salesOrdersWithoutFulfillmentPlanIds = useSelector(Global.selectCustomerOrdersGridSalesOrders);
  const isLoading = useSelector(Global.selectCustomerOrdersGridIsLoading);
  const totalCount = useSelector(Global.selectCustomerOrdersGridTotalCount);
  const fulfillmentPlans = useSelector(Global.selectCustomerOrdersGridFulfillmentPlans);

  const salesOrders = useMemo(() => {
    const fulfillmentPlanLookupHash = fulfillmentPlans.reduce((acc, fulfillmentPlan) => {
      const subAcc = fulfillmentPlan.salesOrdersFulfilledIds.reduce((a, soId) => {
        return {
          ...a,
          [soId]: fulfillmentPlan.fulfillmentPlanId,
        };
      }, {});
      return {
        ...acc,
        ...subAcc,
      };
    }, {});

    const hydratedSalesOrders = salesOrdersWithoutFulfillmentPlanIds.map((order) => {
      return { ...order, id: fulfillmentPlanLookupHash[order.salesOrderId] };
    });

    return hydratedSalesOrders;
  }, [salesOrdersWithoutFulfillmentPlanIds, fulfillmentPlans]);

  if (!hasOrderManagementRole) {
    return (
      <div className={wrapperClassName}>
        <PermissionMessage />
      </div>
    );
  }

  const isEmptyOrders = !isLoading && salesOrders.length === 0;
  return (
    <div className={wrapperClassName}>
      <div className={classnames(style['order-item-container'], style['side-rails'], containerClassName)}>
        <HeaderRow />
        {isLoading && (
          <div className={style['status-container']}>
            <LoadingIndicator />
          </div>
        )}
        {!isLoading && salesOrders.map((order, index) => <OrderDetail key={index} order={order} />)}
      </div>
      {isEmptyOrders && (
        <div className={style['empty-state']}>
          <FormattedMessage {...messages.noPreviousOrders} />
        </div>
      )}
      <div className={style['order-items-footer']}>
        <FormattedMessage {...messages.totalCount} values={{ totalCount }} />
      </div>
    </div>
  );
};
